export interface HsGeometryDownloadGroup<Kind extends string> {
  kind: Kind;
  geometryIds: string[];
  batchSize?: number;
  targetBytes?: number;
  maxBatchSize?: number;
}

const MAX_CONCURRENT_GEOMETRY_DOWNLOADS: number = 4;

export async function hsDownloadGeometryBatches<Kind extends string>(
  groups: HsGeometryDownloadGroup<Kind>[],
  batchSize: number,
  loadAndApply: (kind: Kind, geometryIds: string[]) => Promise<number | void>,
  shouldContinue: () => boolean,
  yieldToBrowser: () => Promise<void>,
  parallelKind?: Kind,
  concurrentKinds: readonly Kind[] = []
): Promise<boolean> {
  if (!Number.isInteger(batchSize) || batchSize < 1) {
    throw new Error('Download batch size must be a positive integer.');
  }

  let failed: boolean = false;
  let activeDownloads: number = 0;
  const waitingForSlot: (() => void)[] = [];
  const acquireSlot = (): Promise<void> | undefined => {
    if (activeDownloads < MAX_CONCURRENT_GEOMETRY_DOWNLOADS) {
      activeDownloads++;
      return undefined;
    }
    return new Promise<void>((resolve: () => void) => waitingForSlot.push(resolve));
  };
  const releaseSlot = (): void => {
    const next: (() => void) | undefined = waitingForSlot.shift();
    if (next !== undefined) {
      next();
    } else {
      activeDownloads--;
    }
  };
  const hasPrimaryWork: boolean = groups.some(
    (group: HsGeometryDownloadGroup<Kind>) => group.kind !== parallelKind && group.geometryIds.length > 0
  );
  const hasParallelWork: boolean = groups.some(
    (group: HsGeometryDownloadGroup<Kind>) => group.kind === parallelKind && group.geometryIds.length > 0
  );
  const processGroups = async (selectedGroups: HsGeometryDownloadGroup<Kind>[]): Promise<boolean> => {
    for (const group of selectedGroups) {
      const groupBatchSize: number = group.batchSize ?? batchSize;
      if (!Number.isInteger(groupBatchSize) || groupBatchSize < 1) {
        throw new Error('Download batch size must be a positive integer.');
      }
      if (
        group.maxBatchSize !== undefined &&
        (!Number.isInteger(group.maxBatchSize) || group.maxBatchSize < groupBatchSize)
      ) {
        throw new Error('Maximum download batch size must be an integer no smaller than the batch size.');
      }
      if (group.targetBytes !== undefined && (!Number.isSafeInteger(group.targetBytes) || group.targetBytes < 1)) {
        throw new Error('Download target bytes must be a positive integer.');
      }
      const uniqueIds: string[] = Array.from(new Set(group.geometryIds));
      let bytesPerGeometry: number | null = null;
      let offset: number = 0;
      const processBatch = async (): Promise<boolean> => {
        if (failed || !shouldContinue()) {
          return false;
        }
        const slot: Promise<void> | undefined = acquireSlot();
        if (slot !== undefined) {
          await slot;
        }
        try {
          if (failed || !shouldContinue()) {
            return false;
          }
          if (offset >= uniqueIds.length) {
            return true;
          }
          const count: number =
            bytesPerGeometry === null || group.targetBytes === undefined
              ? groupBatchSize
              : Math.max(
                  groupBatchSize,
                  Math.min(group.maxBatchSize ?? 10000, Math.ceil(group.targetBytes / bytesPerGeometry))
                );
          const batch: string[] = uniqueIds.slice(offset, offset + count);
          offset += batch.length;
          const bytes: number | void = await loadAndApply(group.kind, batch);
          if (group.targetBytes !== undefined) {
            if (typeof bytes !== 'number' || !Number.isSafeInteger(bytes) || bytes < 1) {
              throw new Error('Adaptive download requires a positive byte length for each completed batch.');
            }
            const measured: number = bytes / batch.length;
            bytesPerGeometry = bytesPerGeometry === null ? measured : (bytesPerGeometry + measured) / 2;
          }
          await yieldToBrowser();
          return !failed && shouldContinue();
        } catch (error: unknown) {
          failed = true;
          throw error;
        } finally {
          releaseSlot();
        }
      };
      if (uniqueIds.length === 0) {
        continue;
      }
      const concurrency: number = !concurrentKinds.includes(group.kind)
        ? 1
        : group.kind !== parallelKind && hasPrimaryWork && hasParallelWork
        ? MAX_CONCURRENT_GEOMETRY_DOWNLOADS - 1
        : MAX_CONCURRENT_GEOMETRY_DOWNLOADS;
      const workers: Promise<boolean>[] = Array.from({ length: concurrency }, async () => {
        while (offset < uniqueIds.length) {
          if (!(await processBatch())) {
            return false;
          }
        }
        return !failed && shouldContinue();
      });
      const results: PromiseSettledResult<boolean>[] = await Promise.allSettled(workers);
      const rejected: PromiseRejectedResult | undefined = results.find(
        (result: PromiseSettledResult<boolean>): result is PromiseRejectedResult => result.status === 'rejected'
      );
      if (rejected !== undefined) {
        throw rejected.reason;
      }
      if (results.some((result: PromiseSettledResult<boolean>) => result.status !== 'fulfilled' || !result.value)) {
        return false;
      }
    }
    return !failed && shouldContinue();
  };

  if (parallelKind === undefined) {
    return processGroups(groups);
  }

  const run = async (selectedGroups: HsGeometryDownloadGroup<Kind>[]): Promise<boolean> => {
    try {
      return await processGroups(selectedGroups);
    } catch (error: unknown) {
      failed = true;
      throw error;
    }
  };
  const results: PromiseSettledResult<boolean>[] = await Promise.allSettled([
    run(groups.filter((group: HsGeometryDownloadGroup<Kind>) => group.kind !== parallelKind)),
    run(groups.filter((group: HsGeometryDownloadGroup<Kind>) => group.kind === parallelKind)),
  ]);
  const rejected: PromiseRejectedResult | undefined = results.find(
    (result: PromiseSettledResult<boolean>): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (rejected !== undefined) {
    throw rejected.reason;
  }
  return results.every((result: PromiseSettledResult<boolean>) => result.status === 'fulfilled' && result.value);
}
